import { useEffect, useRef, useState } from 'react';
import { Button, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocRow,
  DocSignatures,
  DocContactQR,
  DocInlineDate,
  DocInlineAresName,
  DocInlineAresLookup,
  SavedDocsButton,
  NewDocButton,
  DraftBanner,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
} from '../../components/document';
import { MapPickerButton } from '../../components/map';
import {
  CompanyPickerButton,
  SaveCompanyButton,
  type AresPickResult,
  type CompanyPickResult,
} from '../../components/ares';
import { useSaveDocument } from '../../api/hooks';
import { useDocEditor } from '../../lib/useDocEditor';
import { useDocDraft } from '../../lib/useDocDraft';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import { useActiveStavba } from '../stavba-context/StavbaContext';

/**
 * Reklamační protokol — "živý dokument".
 * Sekce: Údaje reklamace / Uplatňující strana / Odpovídající strana /
 * Předmět reklamace / Vypořádání a stanovisko / Termíny / Podpisy.
 */

const STAN_BEZ = '☑ Objednatel souhlasí s provedeným odstraněním vady bez výhrad.';
const STAN_VYHRADY =
  '☑ Objednatel souhlasí s provedeným odstraněním vady s následujícími výhradami:\n— ';

export function ReklamacePage() {
  const saveDoc = useSaveDocument();
  const toast = useToast();
  const { activeStavba } = useActiveStavba();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const [cislo, setCislo] = useState('');
  const [datumZjisteni, setDatumZjisteni] = useState('');
  const [terminOdstraneni, setTerminOdstraneni] = useState('');
  const [datumDoruceni, setDatumDoruceni] = useState('');
  const [zakazka, setZakazka] = useState('');
  const [datumSepsani, setDatumSepsani] = useState('');
  const [nazevStavby, setNazevStavby] = useState('');
  const [smlouva, setSmlouva] = useState('');

  const [upl, setUpl] = useState({
    nazev: '',
    sidlo: '',
    ico: '',
    dic: '',
    tel: '',
    email: '',
  });
  const [odp, setOdp] = useState({
    nazev: '',
    sidlo: '',
    ico: '',
    dic: '',
    tel: '',
    email: '',
  });

  const [vada, setVada] = useState({
    misto: '',
    popis: '',
    zjisteni: '',
    pozadavek: '',
  });

  const [zpusobOdstraneni, setZpusobOdstraneni] = useState('');
  const [datumOdstraneni, setDatumOdstraneni] = useState('');
  const [stanoviskoTyp, setStanoviskoTyp] = useState<'bez' | 'vyhrady'>('bez');
  const [stanovisko, setStanovisko] = useState(STAN_BEZ);
  const [dalsi, setDalsi] = useState('Bez dalších ujednání');

  const onStanoviskoTyp = (t: 'bez' | 'vyhrady') => {
    setStanoviskoTyp(t);
    setStanovisko(t === 'bez' ? STAN_BEZ : STAN_VYHRADY);
  };

  const [podpisy, setPodpisy] = useState({
    uplatnujici: { jmeno: '', funkce: 'investor', image: '' },
    odpovidajici: { jmeno: '', funkce: 'zhotovitel', image: '' },
  });

  // Skryté řádky — uživatel může jednotlivé řádky odebrat ✕,
  // skupinové „obnovit" je v hlavičce sekce. Plus vlastní řádky v sekcích.
  const rows = useDocRows();
  const { isHidden, hide, restoreSection, hiddenCount } = rows;

  const setUplPatch = (patch: Partial<typeof upl>) => setUpl((u) => ({ ...u, ...patch }));
  const setOdpPatch = (patch: Partial<typeof odp>) => setOdp((o) => ({ ...o, ...patch }));
  const setVadaPatch = (patch: Partial<typeof vada>) => setVada((v) => ({ ...v, ...patch }));

  /** Předvyplní formulář ze snímku dat (dokument z archivu i lokální koncept). */
  function applySnapshot(d: Record<string, unknown>) {
    if (d.cislo != null) setCislo(String(d.cislo));
    if (d.datumZjisteni != null) setDatumZjisteni(String(d.datumZjisteni));
    if (d.terminOdstraneni != null) setTerminOdstraneni(String(d.terminOdstraneni));
    if (d.datumDoruceni != null) setDatumDoruceni(String(d.datumDoruceni));
    if (d.zakazka != null) setZakazka(String(d.zakazka));
    if (d.datumSepsani != null) setDatumSepsani(String(d.datumSepsani));
    if (d.nazevStavby != null) setNazevStavby(String(d.nazevStavby));
    if (d.smlouva != null) setSmlouva(String(d.smlouva));
    if (d.upl) setUpl((x) => ({ ...x, ...(d.upl as Partial<typeof upl>) }));
    if (d.odp) setOdp((x) => ({ ...x, ...(d.odp as Partial<typeof odp>) }));
    if (d.vada) setVada((x) => ({ ...x, ...(d.vada as Partial<typeof vada>) }));
    if (d.zpusobOdstraneni != null) setZpusobOdstraneni(String(d.zpusobOdstraneni));
    if (d.datumOdstraneni != null) setDatumOdstraneni(String(d.datumOdstraneni));
    if (d.stanoviskoTyp === 'bez' || d.stanoviskoTyp === 'vyhrady') setStanoviskoTyp(d.stanoviskoTyp);
    if (d.stanovisko != null) setStanovisko(String(d.stanovisko));
    if (d.dalsi != null) setDalsi(String(d.dalsi));
    if (d.podpisy) setPodpisy((p) => ({ ...p, ...(d.podpisy as Partial<typeof podpisy>) }));
    rows.apply(d.radky);
  }

  const docData = {
    cislo, datumZjisteni, terminOdstraneni, datumDoruceni, zakazka, datumSepsani,
    nazevStavby, smlouva, upl, odp, vada, zpusobOdstraneni, datumOdstraneni,
    stanoviskoTyp, stanovisko, dalsi, podpisy,
    radky: rows.snapshot(),
  };
  const { editId } = useDocEditor(applySnapshot);

  // Autosave rozpracovaného konceptu do prohlížeče (jen u nového dokumentu) —
  // bez něj se při zavření/reloadu záložky ztratil celý rozepsaný formulář.
  const draft = useDocDraft<Record<string, unknown>>('reklamace', editId == null);
  function restoreDraft() {
    if (!draft.pending) return;
    applySnapshot(draft.pending.data);
    draft.accept();
  }
  useEffect(() => {
    draft.persist(docData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cislo, datumZjisteni, terminOdstraneni, datumDoruceni, zakazka, datumSepsani, nazevStavby, smlouva, upl, odp, vada, zpusobOdstraneni, datumOdstraneni, stanoviskoTyp, stanovisko, dalsi, podpisy, rows]);

  /**
   * Uložení do archivu. Chybu je nutné ukázat — dřív se `mutateAsync` odmítlo
   * bez ošetření a uživatel si myslel, že je dokument uložený.
   */
  async function handleSave() {
    try {
      await saveDoc.mutateAsync({
        id: editId ?? undefined,
        doc_type: 'reklamace',
        title: `Reklamační protokol ${cislo || nazevStavby || ''}`.trim(),
        stavba: activeStavba?.nazev ?? nazevStavby,
        data_json: docData,
      });
      draft.clear();
      toast.success('Reklamační protokol uložen do archivu.');
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
    }
  }

  return (
    <>
      <DocLayout
        breadcrumb={['Dokumenty', 'Reklamační protokol']}
        title={`Reklamační protokol ${cislo}`}
        statusText="Rozpracovaný koncept"
        status="draft"
        actions={
          <>
            <NewDocButton draftKey="reklamace" path="/app/reklamace" />
            <SavedDocsButton
              docType="reklamace"
              title="Uložené reklamace"
              currentId={editId}
            />
            <Button
              variant="secondary"
              leading="⎙"
              onClick={() => printToPdf(`Reklamace_${cislo}.pdf`)}
            >
              PDF
            </Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saveDoc.isPending}>
              {saveDoc.isPending ? 'Ukládám…' : saveDoc.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        {draft.pending && (
          <DraftBanner
            savedAt={draft.pending.savedAt}
            onRestore={restoreDraft}
            onDiscard={draft.clear}
          />
        )}
        <div ref={previewRef}>
          <DocPage
            company={{
              initials: 'SN',
              name: odp.nazev || '—',
              address: `${odp.sidlo || '—'}${odp.ico ? ' · IČ ' + odp.ico : ''}`,
            }}
            tag="Reklamační protokol"
            title={
              <>
                Reklamační protokol {cislo}{' '}
                <span style={{ fontWeight: 400, fontSize: 14, color: 'var(--sd-doc-dim)' }}>
                  · uplatnění vady
                </span>
              </>
            }
            meta={`Zjištěno ${datumZjisteni} · Doručeno ${datumDoruceni}`}
            footer={{ left: odp.nazev || '—', center: 'str. 1 / 1', right: cislo }}
          >
            <DocBlock
              idx="01"
              title="Údaje reklamace"
              hiddenCount={hiddenCount('s01')}
              onRestoreHidden={() => restoreSection('s01')}
              actions={<DocAddRowButton rows={rows} section="s01" />}
            >
              {!isHidden('s01.cislo') && (
                <DocRow
                  k="Číslo reklamace"
                  mono
                  value={cislo}
                  onChange={setCislo}
                  onRemove={() => hide('s01.cislo')}
                />
              )}
              {!isHidden('s01.zjisteni') && (
                <DocRow
                  k="Datum zjištění"
                  mono
                  value={datumZjisteni}
                  onChange={setDatumZjisteni}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate value={datumZjisteni} onChange={setDatumZjisteni} />
                  }
                  onRemove={() => hide('s01.zjisteni')}
                />
              )}
              {!isHidden('s01.termin') && (
                <DocRow
                  k="Termín odstranění"
                  mono
                  value={terminOdstraneni}
                  onChange={setTerminOdstraneni}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={terminOdstraneni}
                      onChange={setTerminOdstraneni}
                    />
                  }
                  onRemove={() => hide('s01.termin')}
                />
              )}
              {!isHidden('s01.sepsani') && (
                <DocRow
                  k="Termín sepsání"
                  mono
                  value={datumSepsani}
                  onChange={setDatumSepsani}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate value={datumSepsani} onChange={setDatumSepsani} />
                  }
                  onRemove={() => hide('s01.sepsani')}
                />
              )}
              {!isHidden('s01.zakazka') && (
                <DocRow
                  k="Číslo zakázky"
                  mono
                  value={zakazka}
                  onChange={setZakazka}
                  onRemove={() => hide('s01.zakazka')}
                />
              )}
              {!isHidden('s01.stavba') && (
                <DocRow
                  k="Název stavby"
                  value={nazevStavby}
                  onChange={setNazevStavby}
                  placeholder="název stavby / akce"
                  onRemove={() => hide('s01.stavba')}
                />
              )}
              {!isHidden('s01.smlouva') && (
                <DocRow
                  k="Smlouva / objednávka"
                  value={smlouva}
                  onChange={setSmlouva}
                  placeholder="č. SoD / objednávky, ze dne…"
                  onRemove={() => hide('s01.smlouva')}
                />
              )}
              <DocCustomRows rows={rows} section="s01" />
            </DocBlock>

            <DocBlock
              idx="02"
              title="Uplatňující strana"
              hiddenCount={hiddenCount('s02')}
              onRestoreHidden={() => restoreSection('s02')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s02" />
                  <CompanyPickerButton
                    preferRole="objednatel"
                    title="Načíst uplatňujícího z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setUpl({
                        nazev: c.nazev,
                        sidlo: c.adresa,
                        ico: c.ico,
                        dic: c.dic,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit uplatňujícího do adresáře"
                    build={() => ({
                      role: 'objednatel',
                      nazev: upl.nazev,
                      ico: upl.ico,
                      dic: upl.dic,
                      adresa: upl.sidlo,
                      kontakt_tel: upl.tel,
                      kontakt_email: upl.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s02.nazev') && (
                <DocRow
                  k="Název / jméno"
                  v={
                    <DocInlineAresName
                      value={upl.nazev}
                      onChange={(v) => setUplPatch({ nazev: v })}
                      onPick={(d: AresPickResult) =>
                        setUplPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s02.nazev')}
                />
              )}
              {!isHidden('s02.sidlo') && (
                <DocRow
                  k="Adresa"
                  value={upl.sidlo}
                  onChange={(v) => setUplPatch({ sidlo: v })}
                  actions={
                    <MapPickerButton
                      value={upl.sidlo}
                      onApply={(v) => setUplPatch({ sidlo: v })}
                      title="Adresa uplatňujícího"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s02.sidlo')}
                />
              )}
              {!isHidden('s02.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={upl.ico}
                  onChange={(v) => setUplPatch({ ico: v })}
                  actions={
                    <DocInlineAresLookup
                      ico={upl.ico}
                      onResult={(d) =>
                        setUplPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s02.ico')}
                />
              )}
              {!isHidden('s02.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={upl.dic}
                  onChange={(v) => setUplPatch({ dic: v })}
                  onRemove={() => hide('s02.dic')}
                />
              )}
              {!isHidden('s02.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={upl.tel}
                  onChange={(v) => setUplPatch({ tel: v })}
                  inputType="tel"
                  onRemove={() => hide('s02.tel')}
                />
              )}
              {!isHidden('s02.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={upl.email}
                  onChange={(v) => setUplPatch({ email: v })}
                  inputType="email"
                  actions={
                    <DocContactQR
                      name={upl.nazev}
                      role="Uplatňující"
                      phone={upl.tel}
                      email={upl.email}
                      title="QR vizitka — uplatňující"
                    />
                  }
                  onRemove={() => hide('s02.email')}
                />
              )}
              <DocCustomRows rows={rows} section="s02" />
            </DocBlock>

            <DocBlock
              idx="03"
              title="Odpovídající strana (zhotovitel)"
              hiddenCount={hiddenCount('s03')}
              onRestoreHidden={() => restoreSection('s03')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s03" />
                  <CompanyPickerButton
                    preferRole="zhotovitel"
                    title="Načíst zhotovitele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setOdp({
                        nazev: c.nazev,
                        sidlo: c.adresa,
                        ico: c.ico,
                        dic: c.dic,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit zhotovitele do adresáře"
                    build={() => ({
                      role: 'zhotovitel',
                      nazev: odp.nazev,
                      ico: odp.ico,
                      dic: odp.dic,
                      adresa: odp.sidlo,
                      kontakt_tel: odp.tel,
                      kontakt_email: odp.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s03.nazev') && (
                <DocRow
                  k="Název"
                  v={
                    <DocInlineAresName
                      value={odp.nazev}
                      onChange={(v) => setOdpPatch({ nazev: v })}
                      onPick={(d: AresPickResult) =>
                        setOdpPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.nazev')}
                />
              )}
              {!isHidden('s03.sidlo') && (
                <DocRow
                  k="Sídlo"
                  value={odp.sidlo}
                  onChange={(v) => setOdpPatch({ sidlo: v })}
                  actions={
                    <MapPickerButton
                      value={odp.sidlo}
                      onApply={(v) => setOdpPatch({ sidlo: v })}
                      title="Sídlo zhotovitele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s03.sidlo')}
                />
              )}
              {!isHidden('s03.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={odp.ico}
                  onChange={(v) => setOdpPatch({ ico: v })}
                  actions={
                    <DocInlineAresLookup
                      ico={odp.ico}
                      onResult={(d) =>
                        setOdpPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.ico')}
                />
              )}
              {!isHidden('s03.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={odp.dic}
                  onChange={(v) => setOdpPatch({ dic: v })}
                  onRemove={() => hide('s03.dic')}
                />
              )}
              {!isHidden('s03.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={odp.tel}
                  onChange={(v) => setOdpPatch({ tel: v })}
                  inputType="tel"
                  onRemove={() => hide('s03.tel')}
                />
              )}
              {!isHidden('s03.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={odp.email}
                  onChange={(v) => setOdpPatch({ email: v })}
                  inputType="email"
                  actions={
                    <DocContactQR
                      name={odp.nazev}
                      role="Odpovídající"
                      phone={odp.tel}
                      email={odp.email}
                      title="QR vizitka — odpovídající"
                    />
                  }
                  onRemove={() => hide('s03.email')}
                />
              )}
              <DocCustomRows rows={rows} section="s03" />
            </DocBlock>

            <DocBlock
              idx="04"
              title="Předmět reklamace / vada"
              hiddenCount={hiddenCount('s04')}
              onRestoreHidden={() => restoreSection('s04')}
            >
              {!isHidden('s04.misto') && (
                <DocRow
                  k="Místo"
                  value={vada.misto}
                  onChange={(v) => setVadaPatch({ misto: v })}
                  actions={
                    <MapPickerButton
                      value={vada.misto}
                      onApply={(v) => setVadaPatch({ misto: v })}
                      title="Místo vady"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s04.misto')}
                />
              )}
              {!isHidden('s04.popis') && (
                <DocRow
                  k="Popis vady"
                  multiline
                  value={vada.popis}
                  onChange={(v) => setVadaPatch({ popis: v })}
                  onRemove={() => hide('s04.popis')}
                />
              )}
              {!isHidden('s04.zjisteni') && (
                <DocRow
                  k="Zjištění"
                  multiline
                  value={vada.zjisteni}
                  onChange={(v) => setVadaPatch({ zjisteni: v })}
                  onRemove={() => hide('s04.zjisteni')}
                />
              )}
              {!isHidden('s04.pozadavek') && (
                <DocRow
                  k="Požadavek na řešení"
                  multiline
                  value={vada.pozadavek}
                  onChange={(v) => setVadaPatch({ pozadavek: v })}
                  onRemove={() => hide('s04.pozadavek')}
                />
              )}
            </DocBlock>

            <DocBlock
              idx="05"
              title="Vypořádání a stanovisko"
              hiddenCount={hiddenCount('s05x')}
              onRestoreHidden={() => restoreSection('s05x')}
              actions={
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: 'var(--sd-doc-dim)',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Stanovisko
                  </span>
                  <select
                    value={stanoviskoTyp}
                    onChange={(e) => onStanoviskoTyp(e.target.value as 'bez' | 'vyhrady')}
                    style={{
                      padding: '3px 6px',
                      border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)',
                      borderRadius: 4,
                      background: '#fff',
                      color: 'var(--sd-doc-ink)',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                    aria-label="Stanovisko k odstranění vad"
                  >
                    <option value="bez">bez výhrad</option>
                    <option value="vyhrady">s výhradami</option>
                  </select>
                </span>
              }
            >
              {!isHidden('s05x.zpusob') && (
                <DocRow
                  k="Způsob odstranění vad"
                  multiline
                  value={zpusobOdstraneni}
                  onChange={setZpusobOdstraneni}
                  placeholder="Stručný popis provedeného odstranění reklamovaných vad…"
                  onRemove={() => hide('s05x.zpusob')}
                />
              )}
              {!isHidden('s05x.datum') && (
                <DocRow
                  k="Datum odstranění vad"
                  mono
                  value={datumOdstraneni}
                  onChange={setDatumOdstraneni}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate value={datumOdstraneni} onChange={setDatumOdstraneni} />
                  }
                  onRemove={() => hide('s05x.datum')}
                />
              )}
              {!isHidden('s05x.stanovisko') && (
                <DocRow
                  k="Stanovisko k odstranění vad"
                  multiline
                  value={stanovisko}
                  onChange={setStanovisko}
                  placeholder="☑ Objednatel souhlasí…"
                  onRemove={() => hide('s05x.stanovisko')}
                />
              )}
              {!isHidden('s05x.dalsi') && (
                <DocRow
                  k="Další ujednání"
                  multiline
                  value={dalsi}
                  onChange={setDalsi}
                  placeholder="Bez dalších ujednání"
                  onRemove={() => hide('s05x.dalsi')}
                />
              )}
            </DocBlock>

            <DocBlock
              idx="06"
              title="Termíny"
              hiddenCount={hiddenCount('s05')}
              onRestoreHidden={() => restoreSection('s05')}
              actions={<DocAddRowButton rows={rows} section="s05" />}
            >
              {!isHidden('s05.termin') && (
                <DocRow
                  k="Termín odstranění"
                  mono
                  value={terminOdstraneni}
                  onChange={setTerminOdstraneni}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={terminOdstraneni}
                      onChange={setTerminOdstraneni}
                    />
                  }
                  onRemove={() => hide('s05.termin')}
                />
              )}
              {!isHidden('s05.doruceni') && (
                <DocRow
                  k="Datum doručení"
                  mono
                  value={datumDoruceni}
                  onChange={setDatumDoruceni}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate value={datumDoruceni} onChange={setDatumDoruceni} />
                  }
                  onRemove={() => hide('s05.doruceni')}
                />
              )}
              <DocCustomRows rows={rows} section="s05" />
            </DocBlock>

            <DocBlock idx="07" title="Podpisy">
              <DocRow
                k="Uplatňující — jméno"
                value={podpisy.uplatnujici.jmeno}
                onChange={(v) =>
                  setPodpisy((p) => ({
                    ...p,
                    uplatnujici: { ...p.uplatnujici, jmeno: v },
                  }))
                }
              />
              <DocRow
                k="Uplatňující — funkce"
                value={podpisy.uplatnujici.funkce}
                onChange={(v) =>
                  setPodpisy((p) => ({
                    ...p,
                    uplatnujici: { ...p.uplatnujici, funkce: v },
                  }))
                }
              />
              <DocRow
                k="Odpovídající — jméno"
                value={podpisy.odpovidajici.jmeno}
                onChange={(v) =>
                  setPodpisy((p) => ({
                    ...p,
                    odpovidajici: { ...p.odpovidajici, jmeno: v },
                  }))
                }
              />
              <DocRow
                k="Odpovídající — funkce"
                value={podpisy.odpovidajici.funkce}
                onChange={(v) =>
                  setPodpisy((p) => ({
                    ...p,
                    odpovidajici: { ...p.odpovidajici, funkce: v },
                  }))
                }
              />
            </DocBlock>

            <DocSignatures
              items={[
                {
                  label: 'Uplatňující',
                  name: podpisy.uplatnujici.jmeno || '—',
                  role: podpisy.uplatnujici.funkce || '',
                  image: podpisy.uplatnujici.image,
                  onSign: (v) =>
                    setPodpisy((p) => ({
                      ...p,
                      uplatnujici: { ...p.uplatnujici, image: v },
                    })),
                },
                {
                  label: 'Odpovídající',
                  name: podpisy.odpovidajici.jmeno || '—',
                  role: podpisy.odpovidajici.funkce || '',
                  image: podpisy.odpovidajici.image,
                  onSign: (v) =>
                    setPodpisy((p) => ({
                      ...p,
                      odpovidajici: { ...p.odpovidajici, image: v },
                    })),
                },
              ]}
            />
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="reklamace"
        docTitle={`Reklamační protokol ${cislo}`}
        defaultTo={odp.email}
        defaultCc={upl.email}
        defaultSubject={`Reklamační protokol ${cislo}`}
        meta={`Uplatňující: ${upl.nazev || '—'}${
          activeStavba?.nazev ? ' · ' + activeStavba.nazev : ''
        }`}
      />
    </>
  );
}
